import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styles from './styles.module.scss'
import api from '../../../helpers/apiRequester'
import { Loading, Report } from 'notiflix'
import { introducerOptions } from '../reportLinksView/data'
import { useAuthUserData } from '../../../authUserProvider'

interface ClientGroupConfig {
    batchName: string
    introducer: string
    limit: number | null
    sDate: string
    eDate: string
    lenders: string[]
}

interface ClientDetails {
    id: number
    title: string
    firstname: string
    lastname: string
    dob: string
    phone: string
    email: string
    house_number: string
    house_name: string
    flat: string
    street: string
    town_city: string
    postcode: string
    introducer: string
    confirmed_lender: string
    batch_name: string
    batch_id: string
    created_at: string
    updated_at: string
    short_link?: string
    open_banking_link?: string
}

const BASE_URL =
    process.env.REACT_APP_API_URL == 'http://localhost:8080/api'
        ? 'http://localhost:8080'
        : 'https://case-delivery.sentinellegal.co.uk'

export const ILClientDetailsView = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const authUserData = useAuthUserData()
    const [loading, setLoading] = useState(false)
    const [clientDetails, setClientDetails] = useState<ClientDetails | null>(null)
    const [authLoading, setAuthLoading] = useState(true)
    const [authError, setAuthError] = useState(false)
    const [dataError, setDataError] = useState(false)

    // Authentication check on component mount
    useEffect(() => {
        const checkAuthentication = async () => {
            try {
                setAuthLoading(true)
                setAuthError(false)

                const response = await api.post('/il-client-details-auth-check')

                if (response.data === 'SUCCESS') {
                    setAuthLoading(false)
                    // Load client details after successful auth
                    if (id) {
                        loadClientDetails()
                    }
                } else {
                    setAuthError(true)
                    setAuthLoading(false)
                }
            } catch (error) {
                console.error('Authentication check failed:', error)
                setAuthError(true)
                setAuthLoading(false)

                // Show authentication error notification
                Report.failure(
                    'Authentication Required',
                    'Your session has expired. Please log in again to continue.',
                    'Okay'
                )
            }
        }

        checkAuthentication()
    }, [id])

    const loadClientDetails = async () => {
        if (!id) return

        setLoading(true)
        setDataError(false)

        try {
            const response = await api.post('/il-client-details', {
                clientId: id,
            })

            if (response.data && response.data.success) {
                setClientDetails(response.data.clientDetails[0] || null)
            } else {
                setDataError(true)
                Report.failure('Error', response.data?.message || 'Failed to load client details', 'Okay')
            }
        } catch (error: any) {
            console.error('Error loading client details:', error)
            setDataError(true)
            Report.failure(
                'Error',
                error.response?.data?.message || error.message || 'An unexpected error occurred',
                'Okay'
            )
        } finally {
            setLoading(false)
        }
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-GB')
    }

    const formatAddress = (client: ClientDetails) => {
        const addressParts = []

        // Combine house number and street without comma
        if (client.house_number && client.street) {
            addressParts.push(`${client.house_number} ${client.street}`)
        } else if (client.house_number) {
            addressParts.push(client.house_number)
        } else if (client.street) {
            addressParts.push(client.street)
        }

        // Add other address components
        if (client.house_name) addressParts.push(client.house_name)
        if (client.flat) addressParts.push(client.flat)
        if (client.town_city) addressParts.push(client.town_city)
        if (client.postcode) addressParts.push(client.postcode)

        return addressParts.join(', ')
    }

    return (
        <div className={styles.ilClientDetailsView}>
            {/* Authentication Loading */}
            {authLoading && (
                <div className={styles.loadingOverlay}>
                    <div className={styles.loadingContent}>
                        <div className={styles.spinner}></div>
                        <p>Verifying authentication...</p>
                    </div>
                </div>
            )}

            {/* Authentication Error */}
            {authError && (
                <div className={styles.authError}>
                    <div className={styles.authErrorContent}>
                        <h3>Authentication Required</h3>
                        <p>Your session has expired. Please log in again to continue.</p>
                        <button onClick={() => (window.location.href = '/login')} className={styles.authButton}>
                            Go to Login
                        </button>
                    </div>
                </div>
            )}

            {/* Data Loading */}
            {loading && (
                <div className={styles.loadingOverlay}>
                    <div className={styles.loadingContent}>
                        <div className={styles.loadingSpinner}></div>
                        <p className={styles.loadingText}>Loading client details...</p>
                    </div>
                </div>
            )}

            {/* Data Error */}
            {dataError && (
                <div className={styles.errorState}>
                    <div className={styles.errorContent}>
                        <h3>Unable to Load Client Details</h3>
                        <p>There was an error loading the client information. Please try again.</p>
                        <button onClick={loadClientDetails} className={styles.retryButton}>
                            Retry
                        </button>
                    </div>
                </div>
            )}

            {/* Client Details Content */}
            {clientDetails && !loading && !dataError && (
                <div className={styles.container}>
                    <button onClick={() => navigate(-1)} className={styles.backButton}>
                        ← Back
                    </button>
                    <div className={styles.header}>
                        <h1 className={styles.title}>
                            {clientDetails.title} {clientDetails.firstname} {clientDetails.lastname}
                        </h1>
                        <p className={styles.subtitle}>Client ID: {clientDetails.id}</p>
                    </div>

                    <div className={styles.singleCard}>
                        {/* Personal Details Section */}
                        <div className={styles.section}>
                            <h2 className={styles.sectionTitle}>Personal Details</h2>
                            <div className={styles.detailsGrid}>
                                <div className={styles.detailItem}>
                                    <label className={styles.detailLabel}>Title</label>
                                    <p className={styles.detailValue}>{clientDetails.title}</p>
                                </div>
                                <div className={styles.detailItem}>
                                    <label className={styles.detailLabel}>Full Name</label>
                                    <p className={styles.detailValue}>
                                        {clientDetails.firstname} {clientDetails.lastname}
                                    </p>
                                </div>
                                <div className={styles.detailItem}>
                                    <label className={styles.detailLabel}>Date of Birth</label>
                                    <p className={styles.detailValue}>{formatDate(clientDetails.dob)}</p>
                                </div>
                            </div>
                        </div>

                        <div className={styles.sectionDivider}></div>

                        {/* Contact Details Section */}
                        <div className={styles.section}>
                            <h2 className={styles.sectionTitle}>Contact Details</h2>
                            <div className={styles.detailsGrid}>
                                <div className={styles.detailItem}>
                                    <label className={styles.detailLabel}>Email Address</label>
                                    <p className={styles.detailValue}>{clientDetails.email}</p>
                                </div>
                                <div className={styles.detailItem}>
                                    <label className={styles.detailLabel}>Phone Number</label>
                                    <p className={styles.detailValue}>{clientDetails.phone}</p>
                                </div>
                            </div>
                        </div>

                        <div className={styles.sectionDivider}></div>

                        {/* Address Section */}
                        <div className={styles.section}>
                            <h2 className={styles.sectionTitle}>Address</h2>
                            <div className={styles.detailsGrid}>
                                <div className={styles.detailItem}>
                                    <label className={styles.detailLabel}>Full Address</label>
                                    <p className={styles.detailValue}>{formatAddress(clientDetails)}</p>
                                    <div className={styles.addressBreakdown}>
                                        <p className={styles.addressBreakdownText}>
                                            <span className={styles.addressField}>House Number:</span>{' '}
                                            {clientDetails.house_number || 'Not provided'}
                                        </p>
                                        <p className={styles.addressBreakdownText}>
                                            <span className={styles.addressField}>House Name:</span>{' '}
                                            {clientDetails.house_name || 'Not provided'}
                                        </p>
                                        <p className={styles.addressBreakdownText}>
                                            <span className={styles.addressField}>Flat:</span>{' '}
                                            {clientDetails.flat || 'Not provided'}
                                        </p>
                                        <p className={styles.addressBreakdownText}>
                                            <span className={styles.addressField}>Street:</span>{' '}
                                            {clientDetails.street || 'Not provided'}
                                        </p>
                                        <p className={styles.addressBreakdownText}>
                                            <span className={styles.addressField}>Town/City:</span>{' '}
                                            {clientDetails.town_city || 'Not provided'}
                                        </p>
                                        <p className={styles.addressBreakdownText}>
                                            <span className={styles.addressField}>Postcode:</span>{' '}
                                            {clientDetails.postcode || 'Not provided'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.sectionDivider}></div>

                        {/* Introducer Information Section */}
                        <div className={styles.section}>
                            <h2 className={styles.sectionTitle}>Introducer Information</h2>
                            <div className={styles.detailsGrid}>
                                <div className={styles.detailItem}>
                                    <label className={styles.detailLabel}>Confirmed Lender</label>
                                    <p className={styles.detailValue}>{clientDetails.confirmed_lender}</p>
                                </div>
                                <div className={styles.detailItem}>
                                    <label className={styles.detailLabel}>Introducer</label>
                                    <p className={styles.detailValue}>{clientDetails.introducer}</p>
                                </div>
                                <div className={styles.detailItem}>
                                    <label className={styles.detailLabel}>Batch ID</label>
                                    <p className={styles.detailValue}>{clientDetails.batch_id}</p>
                                </div>
                                <div className={styles.detailItem}>
                                    <label className={styles.detailLabel}>Batch Name</label>
                                    <p className={styles.detailValue}>{clientDetails.batch_name}</p>
                                </div>
                            </div>
                        </div>

                        {/* Open Banking Links Section */}
                        {(clientDetails.short_link || clientDetails.open_banking_link) && (
                            <>
                                <div className={styles.sectionDivider}></div>
                                <div className={styles.section}>
                                    <h2 className={styles.sectionTitle}>Open Banking Links</h2>
                                    <div className={styles.linksContainer}>
                                        {clientDetails.short_link && (
                                            <div className={styles.linkItem}>
                                                <label className={styles.detailLabel}>Short Link</label>
                                                <div className={styles.linkContainer}>
                                                    <p className={styles.linkText}>{clientDetails.short_link}</p>
                                                    <div className={styles.linkActions}>
                                                        <a
                                                            href={clientDetails.short_link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={styles.linkButton}
                                                        >
                                                            Open Link
                                                        </a>
                                                        <button
                                                            onClick={() =>
                                                                clientDetails.short_link &&
                                                                navigator.clipboard.writeText(clientDetails.short_link)
                                                            }
                                                            className={styles.copyButton}
                                                            title="Copy link"
                                                        >
                                                            Copy
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {clientDetails.open_banking_link && (
                                            <div className={styles.linkItem}>
                                                <label className={styles.detailLabel}>Open Banking Link</label>
                                                <div className={styles.linkContainer}>
                                                    <p className={styles.linkText}>{clientDetails.open_banking_link}</p>
                                                    <div className={styles.linkActions}>
                                                        <a
                                                            href={clientDetails.open_banking_link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={styles.linkButton}
                                                        >
                                                            Open Link
                                                        </a>
                                                        <button
                                                            onClick={() =>
                                                                clientDetails.open_banking_link &&
                                                                navigator.clipboard.writeText(
                                                                    clientDetails.open_banking_link
                                                                )
                                                            }
                                                            className={styles.copyButton}
                                                            title="Copy link"
                                                        >
                                                            Copy
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        <div className={styles.sectionDivider}></div>

                        {/* System Information Section */}
                        <div className={styles.section}>
                            <h2 className={styles.sectionTitle}>System Information</h2>
                            <div className={styles.detailsGrid}>
                                <div className={styles.detailItem}>
                                    <label className={styles.detailLabel}>Date Added</label>
                                    <p className={styles.detailValue}>{formatDate(clientDetails.created_at)}</p>
                                </div>
                                <div className={styles.detailItem}>
                                    <label className={styles.detailLabel}>Last Updated</label>
                                    <p className={styles.detailValue}>{formatDate(clientDetails.updated_at)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
